import generateFilterConfigs from "../../utils/FilterConfigGenerator";
import React, { useContext, useEffect, useState } from "react";
import FilterComponent from "../../components/complex/crud/FilterComponent";
import { Paginator } from "primereact/paginator";

import { useDispatch } from "react-redux";
import UiContext from "../../store/ui-context";
import useAPIRequest from "../../custom_hooks/simple/useAPIRequest";
import { setLayoutDashboardTitle } from "../../store/ui-slice";
import { DataTable } from "primereact/datatable";
import {
  genTemplateBodyNumber,
  genTemplateBodyText,
} from "../../components/utils/TableTemplateBodyGenerator";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import createFilterText from "../../utils/CreateFilterText";
import { Tag } from "primereact/tag";
import { format } from "date-fns";
import useIsMobile from "../../hooks/useIsMobile";

const title = "Warning Service";

/**
 * Generate Filter Component
 *
 * available options for type:
 * text_plain
 * number_plain
 * date
 */
const filterConfigs = generateFilterConfigs([
  {
    name: "Nomor Polisi",
    field: "data_kendaraan.nomor_polisi",
    type: "text_plain",
    filter: true,
  },
  {
    name: "Jenis Mobil",
    field: "data_kendaraan.jenis_mobil",
    type: "text_plain",
    filter: true,
  },
  {
    name: "Jenis Service",
    field: "data_service.nama",
    type: "text_plain",
    filter: true,
  },
]);


const WarningServicePage = (props, { className }) => {
  const isMobile = useIsMobile();
  const uiContext = useContext(UiContext);
  const dispatch = useDispatch();
  const { requestGet, requestGetOne, requestPost, requestDelete, loading } =
    useAPIRequest();

  const [firstLoad, setFirstLoad] = useState(false);
  const [paging, setPaging] = useState({
    page: 1,
    take: 1000,
    filter: "",
    total: 0,
  });
  const [listData, setListData] = useState([]);

  // useEffect section

  useEffect(() => {
    dispatch(setLayoutDashboardTitle({ title }));
    fetchListData({ page: 1, take: 1000, filter: "" });
  }, []);

  // API call section

  const fetchListData = async ({ page, take, filter }) => {
    if (loading) return;

    const _filter = filter || "";

    return requestGet({
      fullUrl: `/api/laporan`,
      params: {
        prefix: "warning_service",
        page,
        take,
        filter: _filter,
        with_kendaraan: 1,
        with_service: 1,
      },
      onSuccess: ({ pagination, message, data }) => {
        setPaging({
          ...paging,
          total: pagination?.total || 0,
          page: pagination?.page || page,
          take: pagination?.take || take,
          filter: _filter,
        });

        const sortedData = [...(data || [])].sort((a, b) => {
          const aKm = a?.data_kendaraan?.speedo_akhir ?? 0;
          const bKm = b?.data_kendaraan?.speedo_akhir ?? 0;
          const aDiff = (a?.next ?? 0) - aKm;
          const bDiff = (b?.next ?? 0) - bKm;
          return aDiff - bDiff; // sort ascending by next - km
        });

        let no = 1;
        sortedData.forEach(item => {
          item.no = no + 1 * (page - 1) * take;
          no++;
          item.id = item.id || item.no;
        });

        setListData(sortedData);
        if (!firstLoad) setFirstLoad(true);
      },
      onError: ({ message }) => uiContext.showErrorMsg("Error", message),
    });
  };

  // handle UI events section

  const handleOnReload = async () => {
    setPaging({ ...paging, filter: "" });
    fetchListData({
      page: paging.page,
      take: paging.take,
      filter: "",
    });
  };

  const handleOnFilter = (filterMap) => {
    const filterString = createFilterText(filterMap);
    setPaging({ ...paging, filter: filterString });
    fetchListData({
      page: paging.page,
      take: paging.take,
      filter: filterString,
    });
  };

  const handleOnPage = (e) => {
    const newPage = e.first / paging.take + 1;
    const newTake = e.rows;

    setPaging({ ...paging, take: newTake, page: newPage });
    fetchListData({ page: newPage, take: newTake, filter: paging.filter });
  };

  // UI section

  /**
   * Generate Table Column
   *
   * various function to generate
   * genTemplateBodyAction({ row, onEdit, onDelete })
   * genTemplateBodyText({ row, field, defaultEmpty })
   * genTemplateBodyNumber({ row, field, defaultEmpty, maxFraction, minFraction })
   * genTemplateBodyDate({ row, field, format })
   * genTemplateBodyImage({ row, field })
   */

  const NoPosJenisBodyTemplate = (row) => {

    const NoPolisi = genTemplateBodyText({ row, field: "data_kendaraan.nomor_polisi", defaultEmpty: "-" });
    const Jenis = genTemplateBodyText({ row, field: "data_kendaraan.jenis_mobil", defaultEmpty: "" });

    return (
      <div className="col-start-start w-full">
        {/* No Polisi */}
        <div style={{ fontSize: "1rem", fontWeight: "600", color: "#000" }}>
          {NoPolisi}
        </div>

        {/* Jenis */}
        <div
          style={{
            fontSize: "0.85rem",
            color: "#6b7280", // gray
          }}
        >
          {Jenis}
        </div>
      </div>
    );
  };

  const serviceNamaBodyTemplate = (row) =>
    genTemplateBodyText({ row, field: "data_service.nama" });

  const KMBodyTemplate = (row) =>
    genTemplateBodyNumber({ row, field: "data_kendaraan.speedo_akhir", defaultEmpty: "-" });

  const lastKMBodyTemplate = (row) =>
    genTemplateBodyNumber({ row, field: "last", defaultEmpty: "-" });

  const nextKMBodyTemplate = (row) =>
    genTemplateBodyNumber({ row, field: "next", defaultEmpty: "-" });

  const batasKMBodyTemplate = (row) => {

    const last = row?.last ?? 0;
    const next = row?.next ?? 0;
    const diff = next - last;

    return genTemplateBodyNumber({ row: { diff }, field: "diff", defaultEmpty: "-" });
  };

  const statusBodyTemplate = (row) => {
    const km = row?.data_kendaraan?.speedo_akhir ?? 0;
    const next = row?.next ?? 0;
    const diff = next - km;

    // format number with thousand separators (dot)
    const formatted = new Intl.NumberFormat("id-ID").format(diff);

    // decide background color
    const bgColor = diff < 0 ? "bg-red-400" : "bg-orange-400";

    return (
      <Tag
        value={formatted}
        className={`${bgColor} text-black font-semibold px-3 pt-2`}
      />
    );
  };

  const mobileBodyTemplate = (row) => {
    const { data_kendaraan, data_service, last } = row;

    const km = row?.data_kendaraan?.speedo_akhir ?? 0;
    const next = row?.next ?? 0;
    const diff = next - km;
    const lastkm = next - last;

    // format number with thousand separators (dot)
    const formatted = new Intl.NumberFormat("id-ID").format(diff);

    // decide background color
    const bgColor = diff < 0 ? "bg-red-400" : "bg-orange-400";

    return (
      <div className="col-start-start w-full gap-1 p-1">
        {/* --- Header (No Polisi & Jenis Mobil) --- */}
        <div className="row-between-center w-full pb-2 border-b border-stone-200">
          <div className="col-start-start">
            <div className="text-base font-bold text-gray-900">
              {data_kendaraan?.nomor_polisi}
            </div>
            <div className="text-xs text-gray-500">
              {data_kendaraan?.jenis_mobil}
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <i className="pi pi-wrench text-gray-500" />
            {data_service?.nama}
          </div>
        </div>

        {/* --- Odometer Info --- */}
        <div className="col-start-start gap-2 pt-3">
          <div className="row-start-center gap-2">
            <i className="pi pi-gauge text-gray-500" />
            <span className="text-sm text-gray-600">
              KM:{" "}
              <span className="font-semibold text-gray-900">
                {data_kendaraan?.speedo_akhir.toLocaleString("id-ID")} KM
              </span>
            </span>
          </div>

          <div className="row-start-center gap-2">
            <i className="pi pi-history text-gray-500" />
            <span className="text-sm text-gray-600">
              Last:{" "}
              <span className="font-semibold text-gray-900">
                {last?.toLocaleString("id-ID")} KM
              </span>
            </span>
          </div>

          <div className="row-start-center gap-2">
            <i className="pi pi-exclamation-triangle text-gray-500" />
            <span className="text-sm text-gray-600">
              Next:{" "}
              <span className="font-semibold text-gray-900">
                {next?.toLocaleString("id-ID")} KM
              </span>
            </span>
          </div>

          <div className="row-start-center gap-2">
            <i className="pi pi-ban text-gray-500" />
            <span className="text-sm text-gray-600">
              Batas KM:{" "}
              <span className="font-semibold text-gray-900">
                {lastkm?.toLocaleString("id-ID")} KM
              </span>
            </span>
          </div>
        </div>

        {/* --- Diff Tag --- */}
        <div className="pt-3">
          <span
            className={`${bgColor} rounded-xl px-3 py-1 text-xs font-bold text-black`}
          >
            {formatted} KM
          </span>
        </div>
      </div>
    );
  };

  const handlePrintTable = () => {
    const tableElement = document.querySelector(".p-datatable"); // target DataTable DOM
    if (!tableElement) {
      uiContext.showErrorMsg("Print Error", "No table found to print.");
      return;
    }

    const printWindow = window.open("", "_blank");
    const currentDate = format(new Date(), "dd MMMM yyyy");

    const htmlContent = `
    <html>
      <head>
        <title>Warning Service Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            color: #000;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          h1 {
            text-align: center;
            margin-bottom: 0;
          }
          p.date {
            text-align: center;
            color: #555;
            margin-top: 4px;
            margin-bottom: 20px;
          }

          table {
            border-collapse: collapse;
            width: 100%;
          }
          th, td {
            border: 1px solid #aaa;
            padding: 6px 8px;
            text-align: left;
            font-size: 12px;
          }
          th {
            background-color: #f3f3f3 !important;
          }

          /* Ensure tag colors show in print */
          .bg-red-400 {
            background-color: #f87171 !important; /* Red */
            color: black !important;
          }
          .bg-orange-400 {
            background-color: #fb923c !important; /* Orange */
            color: black !important;
          }

          /* PrimeReact Tag component */
          .p-tag {
            display: inline-block;
            border-radius: 12px;
            padding: 4px 10px;
            font-weight: bold;
          }

          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <h1>Warning Service Report</h1>
        <p class="date">${currentDate}</p>
        ${tableElement.outerHTML}
      </body>
    </html>
  `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <div className={`col-start-start p-4 gap-4 w-100`}>

      <div className="elevated-card-tight-vertical w-100">
        <div className="row-start-center w-full gap-2">
          <div className="grow-1">
            <FilterComponent onFilter={handleOnFilter} configs={filterConfigs} />
          </div>
          <div className="flex gap-2">
            {/* Print Button */}
            {!isMobile &&
              <Button
                rounded
                outlined
                icon="pi pi-print"
                onClick={handlePrintTable}
                style={{ width: "40px", height: "40px" }}
                loading={loading}
              />
            }
            {/* Refresh Button */}
            <Button
              rounded
              outlined
              icon="pi pi-refresh"
              onClick={handleOnReload}
              style={{ width: "40px", height: "40px" }}
            />
          </div>
        </div>
      </div>

      <div className="elevated-card col-center-center w-100 gap-3">

        <div className={`col-center-center gap-2 w-100`}>
          {!isMobile && (
            <>
              <DataTable
                dataKey="id"
                className="w-100"
                loading={loading}
                showGridlines
                stripedRows
                editMode="cell"
                value={listData}
              >
                <Column
                  field="no"
                  header="#"
                  className="text-center"
                  headerClassName="w-[4rem]"
                  alignHeader={"center"}
                />
                <Column header="Nomor Polisi" body={NoPosJenisBodyTemplate} />
                <Column field="data_service.nama" header="Jenis Service" body={serviceNamaBodyTemplate} />
                <Column field="data_kendaraan.speedo_akhir" header="KM" body={KMBodyTemplate} bodyStyle={{ textAlign: "right" }} alignHeader={"right"} />
                <Column field="last" header="Last" body={lastKMBodyTemplate} bodyStyle={{ textAlign: "right" }} alignHeader={"right"} />
                <Column field="next" header="Next" body={nextKMBodyTemplate} bodyStyle={{ textAlign: "right" }} alignHeader={"right"} />
                <Column field="batas" header="Batas KM" body={batasKMBodyTemplate} bodyStyle={{ textAlign: "right" }} alignHeader={"right"} />
                <Column field="status" header="Status" body={statusBodyTemplate} bodyStyle={{ textAlign: "right" }} alignHeader={"right"} />
              </DataTable>
              {/* <Paginator
                first={parseInt(paging.take) * (paging.page - 1)}
                rows={parseInt(paging.take)}
                totalRecords={parseInt(paging.total)}
                rowsPerPageOptions={[5, 10, 20, 50]}
                onPageChange={handleOnPage}
              /> */}
            </>
          )}
          {isMobile && (
            <>
              <DataTable
                dataKey="id"
                className="w-100"
                loading={loading}
                showGridlines
                stripedRows
                value={listData}
              >
                <Column
                  header="Deskripsi Warning Service"
                  body={mobileBodyTemplate}
                />
              </DataTable>
              {/* <Paginator
                first={parseInt(paging.take) * (paging.page - 1)}
                rows={parseInt(paging.take)}
                totalRecords={parseInt(paging.total)}
                rowsPerPageOptions={[5, 10, 20, 50]}
                template={{
                  layout: "PrevPageLink CurrentPageReport NextPageLink",
                }}
                onPageChange={handleOnPage}
                pt={{
                  prevPageButton: {
                    className:
                      "bg-white-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
                  },
                  nextPageButton: {
                    className:
                      "bg-white-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
                  },
                  current: {
                    className: "bg-white-100 !m-0 max-h-[22px] text-sm",
                  },
                }}
              /> */}
            </>
          )}
        </div>


      </div>
    </div>
  );
};

export default WarningServicePage;
